import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useUser } from 'global/UserContext';
import { apiStrapi } from 'services/apiService';
import { Menu, MenuItem, MenuResponse } from 'types/menuTypes';
import { SideMenuConfig } from 'types/userPlanFeaturesTypes';
import { getStoredHeaders } from 'utils/headersManager';
import { isValidMenuRoute } from 'utils/menuUtils';

interface MenuContextProps {
  activeMenu: string;
  setActiveMenu: (menu: string) => void;
  activeSubmenu: string;
  setActiveSubmenu: (submenu: string) => void;
  menu: Menu;
  setMenu: (menu: Menu) => void;
  isLoading: boolean;
  error: string | null;
  clearMenu: () => void;
  refreshMenu: () => Promise<void>;
  sideMenu?: SideMenuConfig | null;
  setSideMenu: (sideMenu: SideMenuConfig) => void;
}

const MenuContext = createContext<MenuContextProps | undefined>(undefined);

const STORAGE_KEY = 'menuData';
const STORAGE_KEY_SIDE_MENU = 'sideMenuData';

// Função utilitária para comparar menus
const isMenuEqual = (a: Menu, b: Menu) => JSON.stringify(a) === JSON.stringify(b);

const isSideMenuEqual = (a: SideMenuConfig, b: SideMenuConfig) =>
  JSON.stringify(a) === JSON.stringify(b);

export const MenuProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token } = useUser();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState<string>('');
  const [activeSubmenu, setActiveSubmenu] = useState<string>('');
  const [menu, setMenu] = useState<Menu>([]);
  const [sideMenu, setSideMenu] = useState<SideMenuConfig>({
    contact_info: false,
    coins: false,
    timer: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Atualiza o menu apenas se for diferente
  const updateMenu = (newMenu: Menu) => {
    if (!isMenuEqual(menu, newMenu)) {
      setMenu(newMenu);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newMenu));
      console.log('[MenuContext] Menu atualizado e salvo no localStorage:', newMenu);
    } else {
      console.log('[MenuContext] Menu recebido é igual ao atual, não atualizando.');
    }
  };

  const updateSideMenu = (newSideMenu: SideMenuConfig) => {
    if (!isSideMenuEqual(sideMenu, newSideMenu)) {
      setSideMenu(newSideMenu);
      localStorage.setItem(STORAGE_KEY_SIDE_MENU, JSON.stringify(newSideMenu));
      console.log('[MenuContext] SideMenu atualizado e salvo no localStorage:', newSideMenu);
    } else {
      console.log('[MenuContext] SideMenu recebido é igual ao atual, não atualizando.');
    }
  };

  const clearMenu = () => {
    console.log('[MenuContext] clearMenu chamado!');
    setMenu([]);
    setActiveMenu('');
    setActiveSubmenu('');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_SIDE_MENU);
  };

  const hasRequiredHeaders = () => {
    const headers = getStoredHeaders();
    const hasRequired = headers.organizationUUID && headers.cognitoUserPool;
    console.log('[MenuContext] Headers disponíveis:', {
      hasOrganizationUUID: !!headers.organizationUUID,
      hasCognitoUserPool: !!headers.cognitoUserPool,
    });
    return hasRequired;
  };

  const refreshMenu = async (shouldNavigate: boolean = false) => {
    if (!token) {
      console.log('[MenuContext] Sem token, pulando refreshMenu');
      return;
    }

    if (!hasRequiredHeaders()) {
      console.log('[MenuContext] Headers necessários não encontrados, aguardando inicialização...');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log('[MenuContext] Iniciando refreshMenu');

      const response = await apiStrapi.get<MenuResponse>('/user-person/menu');
      const menuData = response.data.modules;
      const sideMenuData = response.data.sideMenu;
      console.log('[MenuContext] Menu recebido da API:', { menuData, sideMenuData });

      if (Array.isArray(menuData) && menuData.length > 0) {
        updateMenu(menuData);
        updateSideMenu(sideMenuData);

        // Se deve navegar, verificar rota atual e redirecionar se necessário
        if (shouldNavigate) {
          const currentPath = window.location.pathname;
          const isPublicRoute = [
            '/login',
            '/register',
            '/forgot-password',
            '/register-iframe',
          ].includes(currentPath);

          // Exclude event-invite paths from automatic redirection
          const isEventInvitePath = currentPath.startsWith('/invite/event/');

          // Verifica se a rota atual é válida baseada no menu
          const isCurrentRouteValid = isValidMenuRoute(menuData, currentPath);

          // Só navega para o primeiro menu se estivermos em rota pública, raiz ou rota inválida (exceto event-invite)
          if (
            !isEventInvitePath &&
            (isPublicRoute || currentPath === '/' || !isCurrentRouteValid)
          ) {
            // Encontrar primeiro menu válido
            const findFirstValidMenu = (items: MenuItem[]): MenuItem | null => {
              for (const item of items) {
                if (item.feature?.is_active) {
                  return item;
                }
                if (item.subMenu) {
                  const found = findFirstValidMenu(item.subMenu);
                  if (found) return found;
                }
              }
              return null;
            };

            const firstValidMenu = findFirstValidMenu(menuData);
            if (firstValidMenu) {
              console.log(
                '[MenuContext] Navegando para primeiro menu válido:',
                firstValidMenu.path
              );
              navigate(firstValidMenu.path, { replace: true });

              // Forçar seleção do menu pelo documentId após navegação
              setTimeout(() => {
                if (firstValidMenu.feature && firstValidMenu.feature.documentId) {
                  const event = new CustomEvent('forceMenuSelection', {
                    detail: firstValidMenu.feature.documentId,
                  });
                  window.dispatchEvent(event);
                }
              }, 100);
            }
          } else {
            console.log('[MenuContext] Mantendo rota atual válida:', currentPath);
          }
        }
      } else {
        console.error('[MenuContext] Formato de menu inválido:', menuData);
        throw new Error('Formato de menu inválido');
      }
    } catch (error: any) {
      console.error('[MenuContext] Erro ao atualizar menu:', error);
      if (error.response?.status === 401) {
        console.log('[MenuContext] Erro 401 - Headers ou token inválidos');
        clearMenu();
      }
      setError('Erro ao carregar o menu');
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega o menu do localStorage quando o componente monta
  useEffect(() => {
    // Busca o token do localStorage como fallback
    const effectiveToken = token || localStorage.getItem('AUTH_TOKEN');
    if (!effectiveToken) {
      // Não limpa o menu automaticamente! Apenas loga.
      console.log('[MenuContext] Token ausente, mas menu NÃO será limpo automaticamente.');
      setIsLoading(false);
      return;
    }

    if (!hasRequiredHeaders()) {
      // Apenas aguarde, não limpe o menu!
      console.log('[MenuContext] Aguardando headers necessários, não limpando menu.');
      return;
    }

    // Só carrega o menu se ele ainda não estiver carregado
    if (menu.length === 0) {
      console.log('[MenuContext] Menu vazio, tentando carregar do localStorage');
      const storedMenu = localStorage.getItem(STORAGE_KEY);
      const storedSideMenu = localStorage.getItem(STORAGE_KEY_SIDE_MENU);

      if (storedMenu) {
        try {
          const parsedMenu = JSON.parse(storedMenu);
          if (Array.isArray(parsedMenu) && parsedMenu.length > 0) {
            updateMenu(parsedMenu);

            // Também carregar sideMenu se disponível
            if (storedSideMenu) {
              try {
                const parsedSideMenu = JSON.parse(storedSideMenu);
                updateSideMenu(parsedSideMenu);
              } catch (error) {
                console.error('[MenuContext] Erro ao parsear sideMenu do localStorage:', error);
              }
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('[MenuContext] Erro ao parsear menu do localStorage:', error);
          clearMenu();
        }
      }
      // Se não tem no localStorage, busca da API
      console.log('[MenuContext] Buscando menu da API');
      refreshMenu().finally(() => setIsLoading(false));
    } else {
      console.log('[MenuContext] Menu já carregado:', menu);
      setIsLoading(false);
    }
  }, [token]);

  // Salva o menu no localStorage sempre que ele mudar
  useEffect(() => {
    if (token && menu.length > 0) {
      const storedMenu = localStorage.getItem(STORAGE_KEY);
      if (!storedMenu || !isMenuEqual(menu, JSON.parse(storedMenu))) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(menu));
        console.log('[MENU CONTEXT] Menu salvo no localStorage:', menu);
      }
    }
  }, [menu, token]);

  // Limpa o menu quando o usuário desloga
  useEffect(() => {
    if (!token) {
      clearMenu();
      console.log('[MENU CONTEXT] Menu limpo por falta de token');
    }
    console.log('[MenuContext] token mudou:', token);
  }, [token]);

  useEffect(() => {
    console.log('[MenuContext][DEBUG] token:', token, 'menu:', menu);
  }, [token, menu]);

  // Listener para o evento de inicialização do UserContext
  useEffect(() => {
    const handleUserContextInitialized = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[MenuContext] UserContext inicializado, carregando menu...', customEvent.detail);
      // Aguardar um pouco para garantir que os headers estão configurados
      setTimeout(async () => {
        await refreshMenu(true); // true para habilitar navegação após carregar menu
      }, 100);
    };

    window.addEventListener('userContextInitialized', handleUserContextInitialized);

    return () => {
      window.removeEventListener('userContextInitialized', handleUserContextInitialized);
    };
  }, [token]);

  console.log('[MENU CONTEXT] MENU FINAL', activeMenu, activeSubmenu);

  return (
    <MenuContext.Provider
      value={{
        activeMenu,
        setActiveMenu,
        activeSubmenu,
        setActiveSubmenu,
        menu,
        setMenu,
        isLoading,
        error,
        clearMenu,
        refreshMenu,
        sideMenu,
        setSideMenu,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu deve ser usado dentro de um MenuProvider');
  }
  return context;
};
